/**
 * Screenshot Button Component
 * Reusable button for capturing screenshots in feedback forms
 */

import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { captureScreenshot, isScreenshotSupported } from '../../utils/screenshot';

interface ScreenshotButtonProps {
  onScreenshotCapture: (screenshot: { fullImage: string; thumbnail: string; metadata: any }) => void;
  existingScreenshot?: string;
  className?: string;
  disabled?: boolean;
}

const ScreenshotButton: React.FC<ScreenshotButtonProps> = ({
  onScreenshotCapture,
  existingScreenshot,
  className,
  disabled = false
}) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [screenshot, setScreenshot] = useState<string | null>(existingScreenshot || null);

  const handleCaptureClick = async () => {
    if (!isScreenshotSupported()) {
      setError('Screenshot capture is not supported in your browser');
      return;
    }

    setIsCapturing(true);
    setError(null);

    try {
      // Hide feedback widget temporarily for clean screenshot
      const feedbackElements = document.querySelectorAll('[data-feedback-widget], [data-feedback-modal], [data-feedback-quick-reactions]');
      feedbackElements.forEach(el => {
        (el as HTMLElement).style.display = 'none';
      });

      // Wait a moment for UI to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture the screenshot
      const result = await captureScreenshot({
        quality: 0.8,
        format: 'webp',
        includeElement: false
      });

      // Restore feedback elements
      feedbackElements.forEach(el => {
        (el as HTMLElement).style.display = '';
      });

      setScreenshot(result.thumbnail);
      onScreenshotCapture(result);
      
    } catch (err) {
      console.error('Screenshot capture failed:', err);
      setError('Failed to capture screenshot. Please try again.');
      
      // Restore feedback elements on error
      const feedbackElements = document.querySelectorAll('[data-feedback-widget], [data-feedback-modal], [data-feedback-quick-reactions]');
      feedbackElements.forEach(el => {
        (el as HTMLElement).style.display = '';
      });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    onScreenshotCapture({ fullImage: '', thumbnail: '', metadata: {} });
  };

  if (screenshot) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="relative inline-block">
          <img 
            src={screenshot} 
            alt="Screenshot preview" 
            className="max-w-full h-auto rounded-lg border border-stone-200 shadow-sm"
            style={{ maxHeight: '200px' }}
          />
          <button
            onClick={handleRemoveScreenshot}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
            title="Remove screenshot"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <p className="text-xs text-slate-600">Screenshot attached • Click × to remove</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <button
        type="button"
        onClick={handleCaptureClick}
        disabled={disabled || isCapturing}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
          disabled || isCapturing
            ? 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed'
            : 'border-stone-300 hover:border-terracotta hover:bg-terracotta-50 text-slate-700 hover:text-terracotta'
        )}
      >
        {isCapturing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-terracotta"></div>
            <span className="text-sm font-medium">Capturing...</span>
          </>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r={1.5}/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <span className="text-sm font-medium">Add Screenshot</span>
          </>
        )}
      </button>

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}

      <p className="text-xs text-slate-500">
        Capture the current page to help illustrate your feedback
      </p>
    </div>
  );
};

export default ScreenshotButton;